import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_VARIANT_SIZES } from '../graphql/product-queries'

// A size picker over the sizes already in use (`variantSizes`), with a
// "new size" escape hatch. The value is the lower-case size string the API
// stores on ProductVariant.size and matches for the Curry Me Club UPGRADE
// reward; '' means no size.
//
// The API normalises on save (trim + lower-case), but the ladder compares raw
// strings, so this keeps the value lower-case as it is typed.

const NEW = '__new__'
const NONE = '__none__'

interface SizeSelectProps {
  value: string
  onChange: (size: string) => void
  id?: string
  /** Offer a "no size" option (the product form) vs. requiring one (the ladder). */
  includeNone?: boolean
  invalid?: boolean
}

const SizeSelect = ({ value, onChange, id, includeNone = false, invalid = false }: SizeSelectProps) => {
  const { data } = useQuery(GET_VARIANT_SIZES)
  const sizes: string[] = data?.variantSizes ?? []

  // A value that is not one of the known sizes (and not empty) is a custom one
  // the operator typed; keep the text box open for it. Picking "New size…"
  // also opens it while the value is still empty.
  const isKnown = value !== '' && sizes.includes(value)
  const [typing, setTyping] = useState(false)
  const showText = typing || (value !== '' && !isKnown)

  const selectValue = showText ? NEW : value === '' ? NONE : value

  const handleSelect = (raw: string) => {
    if (raw === NEW) {
      setTyping(true)
      onChange('')
    } else if (raw === NONE) {
      setTyping(false)
      onChange('')
    } else {
      setTyping(false)
      onChange(raw)
    }
  }

  const border = invalid ? 'border-red-300 bg-red-50' : 'border-gray-300'

  return (
    <div className="space-y-2">
      <select
        id={id}
        value={selectValue}
        onChange={(e) => handleSelect(e.target.value)}
        className={`w-full px-4 py-3 border ${border} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent hover:border-gray-400 transition-colors`}
      >
        <option value={NONE}>{includeNone ? 'No size' : 'Choose a size…'}</option>
        {sizes.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
        <option value={NEW}>New size…</option>
      </select>
      {showText && (
        <input
          type="text"
          autoFocus
          maxLength={32}
          value={value}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          placeholder="e.g. medium, 330ml"
          className={`w-full px-4 py-3 border ${border} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent hover:border-gray-400 transition-colors`}
        />
      )}
    </div>
  )
}

export default SizeSelect
